import { apiClient } from './client'
import { ApiError } from './problem'
import type { CreditInstrumentAdjustmentView, CreditInstrumentView } from '../types/credit'

/**
 * `GET /api/v1/program-filings/{programFilingId}/credit-instrument` (GA demo v4) — a thin,
 * additive read this story wires onto the already-published `credit.api.CreditInstrumentApi
 * .getByProgramFilingId` port (`CertificationController`). Returns `null` (never throws) on a
 * `404` — a filing that has not been certified yet is a legitimate, common state (an
 * "empty, never an exception" contract mirroring the backend port's own), not a fetch error; any
 * other failure still propagates as `ApiError` for the caller's normal error state.
 */
export async function getCreditInstrumentByFiling(
  programFilingId: string,
  signal?: AbortSignal,
): Promise<CreditInstrumentView | null> {
  try {
    const { data } = await apiClient.get<CreditInstrumentView>(
      `/program-filings/${programFilingId}/credit-instrument`,
      { signal },
    )
    return data
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null
    }
    throw err
  }
}

/**
 * `GET /api/v1/credit-instruments/{id}/adjustments` (`CreditInstrumentAdjustmentHistoryController`,
 * docs/adr-log.md ADR-0041, IB-282 View 2 AC2.5/RAID R-UX282-3 — a small, additive read this story
 * adds, mirroring `listSpendLineAdjustments` exactly) — a **read-only** GET wrapper. Newest-first,
 * matching the backend's own ordering; this wrapper does not re-sort.
 */
export async function listCreditInstrumentAdjustments(
  creditInstrumentId: string,
  signal?: AbortSignal,
): Promise<CreditInstrumentAdjustmentView[]> {
  const { data } = await apiClient.get<CreditInstrumentAdjustmentView[]>(
    `/credit-instruments/${creditInstrumentId}/adjustments`,
    { signal },
  )
  return data
}
